import { useWindowResize, getTextWidth } from "helpers/utility/utilities";
import TextTooltip from "views/components/TextTooltip";
import {
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import { CardVariant } from "types";

interface SingleLabelProps {
  label: string;
  className?: string;
  showTooltip?: boolean;
  onClick: (e: MouseEvent) => void;
}

const SingleLabel = ({
  label,
  className,
  showTooltip,
  onClick,
}: SingleLabelProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={labelRef}
      className={className}
      onClick={onClick}
      onMouseOver={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <span className={styles.labelText}>{label}</span>
      {isHovering && showTooltip && (
        <TextTooltip text={label} parentRef={labelRef} />
      )}
    </div>
  );
};

export interface LabelRowWithOverflowCountProps {
  labels: string[];
  cardVariant: CardVariant;
  selectedLabels: string[];
  onLabelSelection?: (value: string) => void;
}

const LabelRowWithOverflowCount = ({
  labels,
  cardVariant,
  selectedLabels,
  onLabelSelection,
}: LabelRowWithOverflowCountProps) => {
  const [isSingleLabelOverflowing, setSingleLabelOverflowing] = useState(false);
  const [showExtraLabels, setShowExtraLabels] = useState(false);
  const [overflowCount, setOverflowCount] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const overflowCountRef = useRef<HTMLSpanElement>(null);
  const width = useWindowResize();

  const visibleLabels = labels.slice(0, labels.length - overflowCount);
  const extraLabels = labels.slice(-overflowCount);
  const showSingleLabel = visibleLabels.length === 1;
  const extraLabelSelected = extraLabels.some((extraLabel) =>
    selectedLabels.includes(extraLabel)
  );

  const extraCountClassnames = classNames(styles.label, styles.extraCount, {
    [styles.selectedExtraCount]: showExtraLabels,
    [styles.selectedLabel]: extraLabelSelected,
  });

  const labelRowClassnames = classNames(styles.labelRow, {
    [styles.exportLabelRow]: cardVariant === CardVariant.SELECTED,
    [styles.similarityLabelRow]: cardVariant === CardVariant.VERTEX,
  });

  const handleLableClick = (e: MouseEvent, label: string) => {
    e.stopPropagation();
    if (onLabelSelection) {
      onLabelSelection(label);
    }
  };

  const handleOutsideClick = (e: any) => {
    if (
      overflowCountRef.current &&
      !overflowCountRef.current.contains(e.target)
    ) {
      setShowExtraLabels(false);
    }
  };

  useLayoutEffect(() => {
    if (!labels.length) {
      setOverflowCount(0);
    }
    const container = containerRef.current;
    const labelsCount = labels.length;
    const LABEL_PADDING = 8 + 8;
    const LABEL_OUTLINE = 2;
    const LABEL_GAP = 8;
    let totalWidth = 0;

    if (container) {
      container.style.visibility = "hidden";
      let availableWidth = container.offsetWidth;
      let overflowCount = labelsCount;
      if (labelsCount > 0) {
        overflowCount = labelsCount;

        for (let i = 0; i < labelsCount; i++) {
          const labelWidth =
            getTextWidth(labels[i]) + LABEL_PADDING + LABEL_OUTLINE;
          const overflowTextWidth =
            getTextWidth(`+${overflowCount}`) + LABEL_PADDING + LABEL_OUTLINE;

          const isSpaceAvailable =
            totalWidth + labelWidth + LABEL_GAP + overflowTextWidth <=
            availableWidth;

          if (isSpaceAvailable) {
            totalWidth += labelWidth + LABEL_GAP; // Increment total width with the gap
            overflowCount--;
            setSingleLabelOverflowing(false);
          } else {
            if (labelsCount === 1) {
              //discard checking for extra space
              const hasSpaceForSingleValue =
                totalWidth + labelWidth <= availableWidth;

              if (!hasSpaceForSingleValue) {
                overflowCount = 0;
                setSingleLabelOverflowing(true);
              } else {
                setSingleLabelOverflowing(false);
              }
            }
            break; // Exit the loop after determining overflow count
          }
        }

        if (labelsCount === overflowCount) {
          overflowCount--;
        }

        setOverflowCount(overflowCount);
        container.style.visibility = "visible";
      }
    }
  }, [labels, width]);

  useEffect(() => {
    if (showExtraLabels) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showExtraLabels]);

  return (
    <div
      ref={containerRef}
      onClick={handleOutsideClick}
      className={labelRowClassnames}
    >
      {visibleLabels.map((label, index) => {
        const isLabelSelected = selectedLabels.includes(label);

        const singleLabelClassnames = classNames(
          styles.label,
          styles.noDetailsClick,
          onLabelSelection ? styles.showCursor : styles.showNoCursor,
          {
            [styles.selectedLabel]: isLabelSelected,
            [styles.hideOverflow]: showSingleLabel,
          }
        );

        return (
          <SingleLabel
            key={label + index}
            label={label}
            className={singleLabelClassnames}
            showTooltip={isSingleLabelOverflowing}
            onClick={(e: MouseEvent) => handleLableClick(e, label)}
          />
        );
      })}
      {overflowCount > 0 && (
        <span
          ref={overflowCountRef}
          data-testid={"extra-count"}
          className={extraCountClassnames}
          onClick={(e) => e.stopPropagation()}
          onMouseOver={() => setShowExtraLabels(true)}
          onMouseLeave={() => setShowExtraLabels(false)}
        >
          <span>+{overflowCount}</span>
          {showExtraLabels && (
            <div className={styles.dropdownWrapper}>
              <div className={styles.dropdownTip}></div>
              <div className={styles.dropdown}>
                <div className={styles.dropdownContent}>
                  {extraLabels.map((label, index) => {
                    const isLabelSelected = selectedLabels.includes(label);

                    const dropdownLabelClassnames = classNames(
                      styles.label,
                      styles.noDetailsClick,
                      onLabelSelection
                        ? styles.showCursor
                        : styles.showNoCursor,
                      {
                        [styles.selectedLabel]: isLabelSelected,
                      }
                    );

                    return (
                      <SingleLabel
                        key={label + index}
                        label={label}
                        className={dropdownLabelClassnames}
                        onClick={(e: MouseEvent) => handleLableClick(e, label)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </span>
      )}
    </div>
  );
};

export default LabelRowWithOverflowCount;
